import type { AreaId, CompanyId, PersonId, SubAreaId } from '@shared/brand';

/**
 * The organisational tree: one company, several areas, and sub areas inside
 * each area. Sub areas are where the real scheduling constraint lives — the cap
 * on how many people may be out at once is a property of the sub area, not of
 * the area or the company.
 *
 * Every entity in this layer is a plain readonly record. Behaviour that spans
 * entities (does this range breach the cap? does this person need two
 * signatures?) lives in `domain/policies`, so an entity never has to know about
 * runtime configuration.
 *
 * `schemaVersion` rides on every record because these documents outlive any
 * one build: a client reading a document written by a newer deploy upgrades it
 * in memory rather than failing.
 */

/** Fields shared by every persisted record. Also the shape the Postgres
 *  migration relies on: surrogate id, both timestamps, version. */
export interface Auditable {
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly schemaVersion: number;
}

export interface Company extends Auditable {
  readonly id: CompanyId;
  /** Commercial name, shown in the shell and in email templates. */
  readonly name: string;
  /**
   * Domains whose Google accounts may hold a session. Plural because companies
   * acquire other companies and end up with two mail domains for a while.
   */
  readonly allowedDomains: readonly string[];
  /** Mailbox that receives HR copies of every document notice. */
  readonly hrEmail: string;
  readonly country: string;
  /** Storage path of the logo, or null to fall back to the bundled mark. */
  readonly logoPath: string | null;
}

export interface Area extends Auditable {
  readonly id: AreaId;
  readonly companyId: CompanyId;
  readonly name: string;
  /** Accent colour, stored rather than derived so a new area is renderable
   *  the moment it is created — no redeploy to add a colour. */
  readonly color: string;
  /** An inactive area keeps its history but accepts no new requests. */
  readonly active: boolean;
  /** Email of the area's HR administrator; empty until one is assigned. */
  readonly adminEmail: string;
}

export interface SubArea extends Auditable {
  readonly id: SubAreaId;
  readonly areaId: AreaId;
  readonly name: string;
  /**
   * How many people may be out on the same day before the sub area is at its
   * cap. The wizard surfaces the conflict rather than blocking outright, because
   * a leader may still approve.
   *
   * **Never below `MIN_CONCURRENT_OUT`.** This comment used to say that zero meant
   * "no simultaneous absence is allowed", and that was off by one: `assessOverlap`
   * counts the *other* people and flags a day when they reach the cap, so one is
   * the value that lets a single person be away and warns about the second. Zero
   * warns about the first — every request, in a sub area with nobody out.
   */
  readonly maxConcurrentOut: number;
  /**
   * Quién responde por la sub área, o null —«Por asignar»—.
   *
   * **No es quién firma.** Eso vive en `personLeaders`, se asigna persona por
   * persona, y es lo único que enruta una solicitud o un correo: ni el cliente ni
   * las functions leen este campo para decidir nada de una ausencia. Éste responde
   * por la cobertura y por el tope de gente fuera, y alimenta la fila «Sub áreas
   * sin responsable» de Inicio y la vista de Áreas de la empresa.
   *
   * El nombre del campo se queda —renombrarlo pediría una migración por una
   * palabra— pero en pantalla ya no dice «líder» en ningún sitio: las dos cosas se
   * llamaban igual y se leían como una sola.
   */
  readonly leadPersonId: PersonId | null;
  readonly color: string;
}

/**
 * Sub-area accent colours from the handoff, used to seed new records and to
 * colour a sub area whose stored colour is missing. Unrecognised names resolve
 * to the neutral fallback, which is what keeps a runtime-created sub area from
 * rendering colourless.
 */
const SEED_SUB_AREA_COLORS: Readonly<Record<string, string>> = {
  'Desarrollo iOS': '#4A6FA5',
  'Desarrollo Flutter': '#12A594',
  Backend: '#7C5CFF',
  "PO's": '#E8930C',
  QA: '#E5484D',
};

/**
 * El valor más bajo que puede tomar `maxConcurrentOut`.
 *
 * Uno, y no cero, porque el tope es una **advertencia** y no un candado: bajarlo a
 * cero no impide que nadie salga —el líder sigue pudiendo aprobar— sino que hace
 * que el asistente avise de un traslape en toda solicitud de esa sub área, con la
 * sub área entera presente. Una advertencia que se dispara siempre es una que se
 * aprende a no ver, y entonces la que de verdad importa se pierde entre las otras.
 *
 * Uno ya expresa lo más estricto que este portal puede decir: una persona fuera
 * está bien, la segunda se avisa.
 *
 * Su espejo en el servidor es el `min` de `requireInteger` en
 * `functions/src/admin/organization.ts`, en las dos callables que lo escriben.
 */
export const MIN_CONCURRENT_OUT = 1;

export const SUB_AREA_FALLBACK_COLOR = '#64748B';

export const seedColorForSubArea = (name: string): string =>
  SEED_SUB_AREA_COLORS[name] ?? SUB_AREA_FALLBACK_COLOR;

/** Resolves the colour to paint a sub area with, tolerating a blank stored value. */
export const subAreaColor = (subArea: Pick<SubArea, 'name' | 'color'> | undefined): string => {
  if (subArea === undefined) return SUB_AREA_FALLBACK_COLOR;
  const stored = subArea.color.trim();
  if (stored.length > 0) return stored;
  return seedColorForSubArea(subArea.name);
};

/**
 * Darker ink for text sitting on a sub-area colour, from the handoff's `INK`
 * map. An unmapped colour returns itself, which stays legible because the
 * palette is uniformly mid-tone.
 */
const INK_BY_COLOR: Readonly<Record<string, string>> = {
  '#12A594': '#0B7466',
  '#E8930C': '#8A5A05',
  '#7C5CFF': '#5B3FD4',
  '#4A6FA5': '#31527E',
  '#38619F': '#2A4E86',
  '#E5484D': '#A82B30',
};

export const inkFor = (color: string): string => INK_BY_COLOR[color.toUpperCase()] ?? color;
